import { useMemo, useState } from 'react';
import type { MenuItem, FlattenedMenuItem, SearchResult } from '../types';

/**
 * 扁平化菜单数据，构建搜索索引
 * 支持两种子菜单字段：listMenu（处理后）和 opinion_list（原始数据）
 * @param parentIcon 父级图标，用于继承
 * @param isUnderFavorite 当前项是否在收藏菜单下
 */
function flattenMenuList(
  menuList: MenuItem[],
  parentBreadcrumbs: string[] = [],
  parentBreadcrumbsEn: string[] = [],
  level: number = 0,
  parentIcon?: string,
  isUnderFavorite: boolean = false,
): FlattenedMenuItem[] {
  const result: FlattenedMenuItem[] = [];

  menuList.forEach((item) => {
    // 跳过收藏菜单及其子项
    if (item.name_en === 'Favorite Page' || isUnderFavorite) {
      return;
    }

    const currentBreadcrumbs = [...parentBreadcrumbs, item.name_cn];
    const currentBreadcrumbsEn = [...parentBreadcrumbsEn, item.name_en];
    // 当前项的图标：优先使用自己的，否则继承父级
    const currentIcon = item.icon || parentIcon;

    // 添加当前项
    result.push({
      ...item,
      icon: currentIcon,
      level,
      parentPath: parentBreadcrumbs.join(' > '),
      breadcrumbs: currentBreadcrumbs,
      breadcrumbs_en: currentBreadcrumbsEn,
    });

    // 递归处理子菜单（兼容 listMenu 和 opinion_list 两种字段）
    const subMenuList = item.listMenu || item.opinion_list || [];
    if (subMenuList.length > 0) {
      result.push(
        ...flattenMenuList(subMenuList, currentBreadcrumbs, currentBreadcrumbsEn, level + 1, currentIcon, false),
      );
    }
  });

  return result;
}

/**
 * 高亮搜索关键词
 */
export function highlightText(text: string, keyword: string): React.ReactNode {
  if (!keyword) return text;

  const index = text.toLowerCase().indexOf(keyword.toLowerCase());
  if (index === -1) return text;

  const before = text.slice(0, index);
  const match = text.slice(index, index + keyword.length);
  const after = text.slice(index + keyword.length);

  return (
    <>
      {before}
      <span className="dbo-navmenu-search-highlight">{match}</span>
      {after}
    </>
  );
}

export function useMenuSearch(menuList: MenuItem[]) {
  const [keyword, setKeyword] = useState('');

  // 扁平化菜单列表
  const flattenedList = useMemo(() => {
    return flattenMenuList(menuList);
  }, [menuList]);

  // 搜索结果
  const searchResults = useMemo(() => {
    if (!keyword.trim()) {
      return [];
    }

    const lowerKeyword = keyword.toLowerCase().trim();
    const results: SearchResult[] = [];

    flattenedList.forEach((item) => {
      // 跳过锁定和禁用的菜单
      if (item.showLock || item.disabled) return;

      // 只展示叶子节点（无子菜单的可跳转项）
      const subMenuList = item.listMenu || item.opinion_list || [];
      if (subMenuList.length > 0) return;

      const nameCnLower = item.name_cn.toLowerCase();
      const nameEnLower = item.name_en.toLowerCase();

      let matchType: 'name_cn' | 'name_en' | null = null;
      let matchIndex = -1;

      // 匹配当前项名称
      if (nameCnLower.includes(lowerKeyword)) {
        matchType = 'name_cn';
        matchIndex = nameCnLower.indexOf(lowerKeyword);
      } else if (nameEnLower.includes(lowerKeyword)) {
        matchType = 'name_en';
        matchIndex = nameEnLower.indexOf(lowerKeyword);
      }
      // 匹配父级路径（breadcrumbs 中的父级名称）
      else if (item.breadcrumbs.some(bc => bc.toLowerCase().includes(lowerKeyword))) {
        matchType = 'name_cn';
        // 父级匹配时，matchIndex 设为较大值，排在直接匹配后面
        matchIndex = 1000;
      }

      if (matchType) {
        results.push({
          ...item,
          matchType,
          matchIndex,
        });
      }
    });

    // 按匹配位置排序
    return results.sort((a, b) => a.matchIndex - b.matchIndex).slice(0, 10);
  }, [keyword, flattenedList]);

  return {
    keyword,
    setKeyword,
    searchResults,
    flattenedList,
    highlightText,
  };
}
